import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError,
  defer,
  finalize,
  from,
  Observable,
  switchMap,
  throwError,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiError, get, post, put, del } from 'aws-amplify/api';
import { NgxSpinnerService } from 'ngx-spinner';
const DEFAULT_TIMEOUT = 90000;

export const API_NAME = 'UpliftApi';
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private activeRequests = 0;

  constructor(
    private httpClient: HttpClient,
    private spinner: NgxSpinnerService
  ) {}

  get<T>(
    path: string,
    options?: any,
    timeoutInMs?: number,
    showSpinner = false,
    responseType: 'json' | 'text' = 'json'
  ): Observable<T> {
    if (showSpinner) this.incrementSpinner();
    const getPromise = get({ apiName: API_NAME, path, options }).response;
    const timeout = timeoutInMs ?? DEFAULT_TIMEOUT;
    const responsePromise = this.promiseWithTimeout(
      getPromise,
      timeout,
      'Request timed out'
    )
      .then(async (response) => {
        const text = await response.body.text();
        return responseType === 'text' ? text : (text ? JSON.parse(text) : null);
      })
      .finally(() => {
        if (showSpinner) this.decrementSpinner();
      })
      .catch((error) => {
        if (error instanceof ApiError && error.response) {
          const { statusCode, body } = error.response;
          throw { status: statusCode, body };
        }
        return this.handleError(error);
      });

    return from(responsePromise);
  }

  post<T>(
    path: string,
    body: any,
    options: any = {},
    showSpinner = false,
    apiName = API_NAME,
    timeoutInMs?: number,
    responseType: 'json' | 'text' = 'json'
  ): Observable<T> {
    return this.sendRequest(
      'POST',
      path,
      body,
      options,
      showSpinner,
      apiName,
      timeoutInMs,
      responseType
    );
  }

  put<T>(
    path: string,
    body: any,
    options: any = {},
    showSpinner = false,
    apiName = API_NAME,
    timeoutInMs?: number
  ): Observable<T> {
    return this.sendRequest(
      'PUT',
      path,
      body,
      options,
      showSpinner,
      apiName,
      timeoutInMs
    );
  }

  delete<T>(
    path: string,
    body?: any,
    options: any = {},
    showSpinner = false,
    apiName = API_NAME,
    timeoutInMs?: number
  ): Observable<T> {
    return this.sendRequest(
      'DELETE',
      path,
      body,
      options,
      showSpinner,
      apiName,
      timeoutInMs
    );
  }

private sendRequest<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body: any,
  options: any = {},
  showSpinner = false,
  apiName = API_NAME,
  timeoutInMs?: number,
  responseType: 'json' | 'text' = 'json'
): Observable<T> {
  if (showSpinner) this.incrementSpinner();

  const requestOptions = body !== undefined ? { ...options, body } : { ...options };
  const request: Promise<any> =
    method === 'POST'
      ? post({ apiName, path, options: requestOptions }).response
      : method === 'PUT'
      ? put({ apiName, path, options: requestOptions }).response
      : del({ apiName, path, options: requestOptions }).response;

  const timeout = timeoutInMs ?? DEFAULT_TIMEOUT;

  const responsePromise = this
    .promiseWithTimeout(request, timeout, 'Request timed out')
    .then(async (res) => {
      const text = await res.body.text();
      return responseType === 'text' ? text : (text ? JSON.parse(text) : null);
    })
    .finally(() => {
      if (showSpinner) this.decrementSpinner();
    })
    .catch((error: any) => {
      if (error?.response) {
        const { statusCode, body: errBody } = error.response;
        throw { status: statusCode, body: errBody };
      }
      return this.handleError(error);
    });

  return from(responsePromise);
}
  private async handleError(error: any): Promise<never> {
    if (error.message === 'Request timed out') {
      throw new Error('Request timed out');
    } else {
      throw new Error(`API Error: ${error.message || 'Unknown error'}`);
    }
  }

  private promiseWithTimeout<T>(
  promise: Promise<T>,
  ms: number,
  timeoutError: string
): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout>;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(timeoutError)), ms);
  });

  return Promise.race([promise, timeoutPromise])
    .then((result) => {
      clearTimeout(timeoutHandle);
      return result;
    })
    .catch((error) => {
      clearTimeout(timeoutHandle);
      throw error;
    });
}

  private incrementSpinner() {
    this.activeRequests++;
    if (this.activeRequests === 1) {
      this.spinner.show();
    }
  }

  private decrementSpinner() {
    this.activeRequests--;
    if (this.activeRequests <= 0) {
      this.activeRequests = 0;
      this.spinner.hide();
    }
  }
}
